import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, vModelText as _vModelText, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, unref as _unref, createVNode as _createVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "py-16 text-center text-gray-400"
}
const _hoisted_2 = {
  key: 1,
  class: "py-16 text-center text-red-500"
}
const _hoisted_3 = {
  key: 2,
  class: "grid grid-cols-1 lg:grid-cols-2 gap-6 items-start"
}
const _hoisted_4 = {
  key: 0,
  class: "text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2"
}
const _hoisted_5 = { class: "flex items-center gap-3 cursor-pointer select-none" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "grid grid-cols-2 gap-4" }
const _hoisted_9 = { class: "block text-sm font-semibold text-gray-700 mb-1" }
const _hoisted_10 = ["onUpdate:modelValue", "disabled"]
const _hoisted_11 = { class: "text-xs text-gray-400 mt-1" }
const _hoisted_12 = {
  key: 1,
  class: "text-xs text-gray-400"
}
const _hoisted_13 = { key: 0 }
const _hoisted_14 = {
  key: 2,
  class: "flex flex-wrap items-center gap-3 pt-2"
}
const _hoisted_15 = ["disabled"]
const _hoisted_16 = ["disabled", "title"]
const _hoisted_17 = {
  key: 0,
  class: "text-emerald-600 text-sm flex items-center gap-1"
}
const _hoisted_18 = {
  key: 1,
  class: "text-red-500 text-sm"
}
const _hoisted_19 = { class: "bg-white rounded-xl border border-gray-200 overflow-hidden" }
const _hoisted_20 = { class: "w-full text-sm" }
const _hoisted_21 = { class: "px-4 py-3 text-gray-500 whitespace-nowrap" }
const _hoisted_22 = { class: "px-4 py-3" }
const _hoisted_23 = { class: "px-4 py-3 font-mono text-xs" }
const _hoisted_24 = { class: "px-4 py-3 text-right" }
const _hoisted_25 = { class: "text-gray-400" }
const _hoisted_26 = { key: 0 }

import { ref, reactive, computed, onMounted } from 'vue'
import { Check, Mail } from 'lucide-vue-next'
import { apiFetch } from '@/utils/apiFetch'

type NumberKey = 'window_minutes' | 'login_failures' | 'otp_failures' | 'access_denials' | 'silence_minutes'

interface RecentAlert {
  created_at: string
  type: string | null
  cle: string | null
  nombre: number | null
  fenetre_minutes: number | null
}

interface AlertConfig extends Record<NumberKey, number> {
  enabled: boolean
  recipients: string[]
  updated_at: string | null
  updated_by: string | null
  can_edit: boolean
  recent_alerts: RecentAlert[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AlertesSecuritePanel',
  setup(__props) {

const numberFields: { key: NumberKey; label: string; help: string }[] = [
  { key: 'login_failures', label: 'Échecs de connexion', help: 'par adresse IP, dans la fenêtre' },
  { key: 'otp_failures', label: 'Échecs de code OTP', help: 'par compte, dans la fenêtre' },
  { key: 'access_denials', label: "Refus d'accès", help: 'par compte, dans la fenêtre' },
  { key: 'window_minutes', label: "Fenêtre d'analyse (min)", help: 'période glissante observée' },
  { key: 'silence_minutes', label: 'Silence (min)', help: 'délai avant de renvoyer la même alerte' },
]

const typeLabels: Record<string, string> = {
  connexions_echouees: 'Échecs de connexion',
  otp_echoue: 'Échecs OTP',
  acces_refuses: "Refus d'accès",
}

const loading = ref(false)
const loadError = ref<string | null>(null)
const saving = ref(false)
const testing = ref(false)
const message = ref<string | null>(null)
const error = ref<string | null>(null)

const canEdit = ref(false)
const updatedAt = ref<string | null>(null)
const updatedBy = ref<string | null>(null)
const savedRecipients = ref<string[]>([])
const recentAlerts = ref<RecentAlert[]>([])
const recipientsText = ref('')
const form = reactive<Record<NumberKey, number> & { enabled: boolean }>({
  enabled: true, window_minutes: 5, login_failures: 10, otp_failures: 5, access_denials: 5, silence_minutes: 60,
})

const recipients = computed(() =>
  recipientsText.value.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean)
)

function apply(data: AlertConfig) {
  form.enabled = data.enabled
  for (const f of numberFields) form[f.key] = data[f.key]
  savedRecipients.value = data.recipients || []
  recipientsText.value = savedRecipients.value.join(', ')
  canEdit.value = data.can_edit
  updatedAt.value = data.updated_at
  updatedBy.value = data.updated_by
  recentAlerts.value = data.recent_alerts || []
}

function flash(text: string) {
  message.value = text
  setTimeout(() => { message.value = null }, 3000)
}

async function fetchConfig() {
  loading.value = true
  try {
    apply(await apiFetch<AlertConfig>('/api/admin/security-alerts/config/'))
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Erreur de chargement.'
  } finally {
    loading.value = false
  }
}

async function saveConfig() {
  saving.value = true
  error.value = null
  try {
    const payload: Record<string, unknown> = { enabled: form.enabled, recipients: recipients.value }
    for (const f of numberFields) payload[f.key] = form[f.key]
    apply(await apiFetch<AlertConfig>('/api/admin/security-alerts/config/', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }))
    flash('Enregistré')
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Échec de l'enregistrement."
  } finally {
    saving.value = false
  }
}

async function sendTest() {
  testing.value = true
  error.value = null
  try {
    const data = await apiFetch<{ sent_to: string[] }>('/api/admin/security-alerts/test/', { method: 'POST' })
    flash(`Email de test envoyé à ${data.sent_to.join(', ')}`)
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Échec de l'envoi."
  } finally {
    testing.value = false
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

onMounted(fetchConfig)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", null, [
    _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-sm text-gray-500 mb-5" }, " Seuils de détection et destinataires des alertes (brute-force, refus d'accès répétés). ", -1)),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, "Chargement…"))
      : (loadError.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(loadError.value), 1))
        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("form", {
              onSubmit: _withModifiers(saveConfig, ["prevent"]),
              class: "bg-white rounded-xl border border-gray-200 p-6 space-y-5"
            }, [
              (!canEdit.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_4, " Lecture seule : seul l'administrateur global peut modifier ces réglages. "))
                : _createCommentVNode("", true),
              _createElementVNode("label", _hoisted_5, [
                _withDirectives(_createElementVNode("input", {
                  type: "checkbox",
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.enabled) = $event)),
                  disabled: !canEdit.value,
                  class: "w-4 h-4"
                }, null, 8, _hoisted_6), [
                  [_vModelCheckbox, form.enabled]
                ]),
                _cache[2] || (_cache[2] = _createElementVNode("span", { class: "text-sm font-semibold text-gray-700" }, "Alertes activées", -1))
              ]),
              _createElementVNode("div", null, [
                _cache[3] || (_cache[3] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 mb-1" }, "Destinataires", -1)),
                _withDirectives(_createElementVNode("textarea", {
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((recipientsText).value = $event)),
                  disabled: !canEdit.value,
                  rows: "3",
                  placeholder: "dsi@gouv.bj, securite@gouv.bj",
                  class: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50"
                }, null, 8, _hoisted_7), [
                  [_vModelText, recipientsText.value]
                ]),
                _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-xs text-gray-400 mt-1" }, " Séparés par des virgules ou des retours à la ligne. Vide : les alertes restent visibles dans le journal d'audit et Sentry, sans email. ", -1))
              ]),
              _createElementVNode("div", _hoisted_8, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(numberFields, (f) => {
                  return _createElementVNode("div", {
                    key: f.key
                  }, [
                    _createElementVNode("label", _hoisted_9, _toDisplayString(f.label), 1),
                    _withDirectives(_createElementVNode("input", {
                      type: "number",
                      min: "1",
                      "onUpdate:modelValue": ($event: any) => ((form[f.key]) = $event),
                      disabled: !canEdit.value,
                      class: "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50"
                    }, null, 8, _hoisted_10), [
                      [
                        _vModelText,
                        form[f.key],
                        void 0,
                        { number: true }
                      ]
                    ]),
                    _createElementVNode("p", _hoisted_11, _toDisplayString(f.help), 1)
                  ])
                }), 64))
              ]),
              (updatedAt.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_12, [
                    _createTextVNode(" Dernière modification le " + _toDisplayString(formatDate(updatedAt.value)), 1),
                    (updatedBy.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_13, " par " + _toDisplayString(updatedBy.value), 1))
                      : _createCommentVNode("", true),
                    _cache[5] || (_cache[5] = _createTextVNode(". "))
                  ]))
                : _createCommentVNode("", true),
              (canEdit.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                    _createElementVNode("button", {
                      type: "submit",
                      disabled: saving.value,
                      class: "px-5 py-2.5 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
                    }, _toDisplayString(saving.value ? 'Enregistrement…' : 'Enregistrer'), 9, _hoisted_15),
                    _createElementVNode("button", {
                      type: "button",
                      onClick: sendTest,
                      disabled: testing.value || savedRecipients.value.length === 0,
                      class: "px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50",
                      title: savedRecipients.value.length === 0 ? 'Enregistrez d\'abord au moins un destinataire' : ''
                    }, [
                      _createVNode(_unref(Mail), { class: "w-4 h-4" }),
                      _createTextVNode(_toDisplayString(testing.value ? 'Envoi…' : 'Envoyer un email de test'), 1)
                    ], 8, _hoisted_16),
                    (message.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_17, [
                          _createVNode(_unref(Check), { class: "w-4 h-4" }),
                          _createTextVNode(" " + _toDisplayString(message.value), 1)
                        ]))
                      : _createCommentVNode("", true),
                    (error.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(error.value), 1))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ], 32),
            _createElementVNode("div", _hoisted_19, [
              _cache[8] || (_cache[8] = _createElementVNode("div", { class: "px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700" }, " Dernières alertes envoyées ", -1)),
              _createElementVNode("table", _hoisted_20, [
                _cache[7] || (_cache[7] = _createElementVNode("thead", null, [
                  _createElementVNode("tr", { class: "bg-gray-50 text-left text-gray-500 text-xs uppercase tracking-wide" }, [
                    _createElementVNode("th", { class: "px-4 py-3 font-semibold whitespace-nowrap" }, "Date"),
                    _createElementVNode("th", { class: "px-4 py-3 font-semibold" }, "Type"),
                    _createElementVNode("th", { class: "px-4 py-3 font-semibold" }, "Cible"),
                    _createElementVNode("th", { class: "px-4 py-3 font-semibold text-right" }, "Nombre")
                  ])
                ], -1)),
                _createElementVNode("tbody", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recentAlerts.value, (a, idx) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      key: idx,
                      class: "border-t border-gray-100"
                    }, [
                      _createElementVNode("td", _hoisted_21, _toDisplayString(formatDate(a.created_at)), 1),
                      _createElementVNode("td", _hoisted_22, _toDisplayString(typeLabels[a.type || ''] || a.type), 1),
                      _createElementVNode("td", _hoisted_23, _toDisplayString(a.type === 'connexions_echouees' ? `IP ${a.cle}` : `compte #${a.cle}`), 1),
                      _createElementVNode("td", _hoisted_24, [
                        _createTextVNode(_toDisplayString(a.nombre) + " ", 1),
                        _createElementVNode("span", _hoisted_25, "/ " + _toDisplayString(a.fenetre_minutes) + " min", 1)
                      ])
                    ]))
                  }), 128)),
                  (recentAlerts.value.length === 0)
                    ? (_openBlock(), _createElementBlock("tr", _hoisted_26, _cache[6] || (_cache[6] = [
                        _createElementVNode("td", {
                          colspan: "4",
                          class: "px-4 py-10 text-center text-gray-400"
                        }, "Aucune alerte envoyée.", -1)
                      ])))
                    : _createCommentVNode("", true)
                ])
              ])
            ])
          ]))
  ]))
}
}

})